import React, { forwardRef, useCallback, useEffect, useState, } from "react";
import { flip, offset, size, useFloating } from "@floating-ui/react-dom";
import { Box, Portal } from "@chakra-ui/react";
export function useSelectPopover() {
    const { x, y, reference, floating, strategy, update } = useFloating({
        placement: "bottom-start",
        middleware: [
            flip(),
            offset(8),
            size({
                apply({ rects, elements }) {
                    elements.floating.style.width = `${rects.reference.width}px`;
                },
            }),
        ],
    });
    const [isOpen, setIsOpen] = useState(false);
    const [isOpenScheduled, setIsOpenScheduled] = useState(false);
    // Don't open and focus the menu immediately and schedule it for later.
    // Popover position must be recalculated first, otherwise Downshift
    // focuses the popover in its old position, causing the page to scroll
    // to the wrong position.
    // See https://floating-ui.com/docs/react-dom#effects.
    const open = useCallback(() => {
        update();
        setIsOpenScheduled(true);
    }, [update]);
    useEffect(() => {
        if (!isOpenScheduled) {
            return;
        }
        setIsOpen(true);
    }, [isOpenScheduled]);
    const close = useCallback(() => {
        setIsOpen(false);
        setIsOpenScheduled(false);
    }, []);
    return {
        isOpen,
        open,
        close,
        update,
        getTargetProps(props) {
            return {
                ref: reference,
                ...props,
            };
        },
        getPopoverProps() {
            return {
                ref: floating,
                left: `${x ?? 0}px`,
                top: `${y ?? 0}px`,
                position: strategy,
            };
        },
    };
}
export const SelectPopover = forwardRef(({ isOpen, children, ...props }, ref) => {
    return (React.createElement(Portal, null,
        React.createElement(Box, { ref: ref, background: "white", border: "1px", borderColor: "gray.400", borderRadius: "md", boxShadow: "lg", outline: "none", display: "flex", flexDirection: "column", overflow: "auto", overscrollBehavior: "contain", transition: isOpen
                ? "opacity 150ms linear, transform 150ms cubic-bezier(0.34, 1.56, 0.64, 1)"
                : undefined, transform: "auto", transformOrigin: "top", translateY: isOpen ? 0 : -6, opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? "auto" : "none", minWidth: "3xs", maxHeight: "320px", zIndex: "popover", ...props }, children)));
});
